import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronRight,
  Zap,
  Users,
  Rocket,
  Crown,
  Star,
  ArrowRight,
  ExternalLink,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { useAuthStore } from '../../store/authStore';
import { socialAPI, usersAPI, getErrorMessage } from '../../services/api';

// ── Plan data ──────────────────────────────────────────

interface PlanBase {
  id: string;
  name: string;
  price: string;
  icon: React.ElementType;
  popular?: boolean;
  profiles: number;
  teamMembers: number;
}

const PLANS_BASE: PlanBase[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    icon: Zap,
    profiles: 2,
    teamMembers: 1,
  },
  {
    id: 'start',
    name: 'Start',
    price: '$15',
    icon: Star,
    profiles: 5,
    teamMembers: 2,
  },
  {
    id: 'smm',
    name: 'SMM',
    price: '$25',
    icon: Users,
    profiles: 10,
    teamMembers: 3,
    popular: true,
  },
  {
    id: 'agency',
    name: 'Agency',
    price: '$65',
    icon: Rocket,
    profiles: 25,
    teamMembers: 10,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '$120',
    icon: Crown,
    profiles: 50,
    teamMembers: 20,
  },
];

// ── Platforms for the connect step ──────────────────────

const POPULAR_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: '📷', color: 'bg-pink-600' },
  { id: 'facebook', name: 'Facebook', icon: 'f', color: 'bg-blue-600' },
  { id: 'twitter', name: 'X (Twitter)', icon: '𝕏', color: 'bg-black' },
  { id: 'tiktok', name: 'TikTok', icon: '♪', color: 'bg-gray-900' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'in', color: 'bg-blue-700' },
  { id: 'youtube', name: 'YouTube', icon: '▶', color: 'bg-red-600' },
  { id: 'pinterest', name: 'Pinterest', icon: 'P', color: 'bg-red-700' },
  { id: 'threads', name: 'Threads', icon: '@', color: 'bg-gray-900' },
  { id: 'bluesky', name: 'Bluesky', icon: '🦋', color: 'bg-blue-400' },
  { id: 'telegram', name: 'Telegram', icon: '✈', color: 'bg-blue-500' },
];

// ── Component ──────────────────────────────────────────

type Step = 'plan' | 'connect' | 'done';

export default function OnboardingWizard() {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('plan');
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);

  // Build translated plan periods
  const planPeriods = useMemo(() => ({
    free: t('onboard_plan_period_forever', 'forever'),
    start: t('onboard_plan_period_mo', '/mo'),
    smm: t('onboard_plan_period_mo', '/mo'),
    agency: t('onboard_plan_period_mo', '/mo'),
    pro: t('onboard_plan_period_mo', '/mo'),
  }), [t]);

  // Build translated plan features
  const planFeatures = useMemo(() => ({
    free: [
      t('onboard_feature_profiles_2', '2 social profiles'),
      t('onboard_feature_posts_10', '10 scheduled posts'),
      t('onboard_feature_pages_1', '1 link-in-bio page'),
      t('onboard_feature_analytics_basic', 'Basic analytics'),
    ],
    start: [
      t('onboard_feature_profiles_5', '5 social profiles'),
      t('onboard_feature_posts_50', '50 scheduled posts'),
      t('onboard_feature_pages_3', '3 link-in-bio pages'),
      t('onboard_feature_team_2', '2 team members'),
      t('onboard_feature_support_priority', 'Priority support'),
    ],
    smm: [
      t('onboard_feature_profiles_10', '10 social profiles'),
      t('onboard_feature_posts_200', '200 scheduled posts'),
      t('onboard_feature_pages_10', '10 link-in-bio pages'),
      t('onboard_feature_team_3', '3 team members'),
      t('onboard_feature_ai_assistant', 'AI assistant'),
    ],
    agency: [
      t('onboard_feature_profiles_25', '25 social profiles'),
      t('onboard_feature_posts_1000', '1,000 scheduled posts'),
      t('onboard_feature_pages_50', '50 link-in-bio pages'),
      t('onboard_feature_team_10', '10 team members'),
      t('onboard_feature_white_label', 'White-label'),
    ],
    pro: [
      t('onboard_feature_profiles_50', '50 social profiles'),
      t('onboard_feature_posts_unlimited', 'Unlimited scheduled posts'),
      t('onboard_feature_pages_100', '100 link-in-bio pages'),
      t('onboard_feature_team_20', '20 team members'),
      t('onboard_feature_api_access', 'API access'),
      t('onboard_feature_support_dedicated', 'Dedicated support'),
    ],
  }), [t]);

  // Build PLANS array with translated data
  const PLANS = useMemo(() => PLANS_BASE.map((plan) => ({
    ...plan,
    period: planPeriods[plan.id as keyof typeof planPeriods],
    features: planFeatures[plan.id as keyof typeof planFeatures],
  })), [planPeriods, planFeatures]);

  // Fetch already-connected accounts
  const { data: accountsData, refetch: refetchAccounts } = useQuery({
    queryKey: ['socialAccounts'],
    queryFn: async () => {
      const { data } = await socialAPI.list();
      return (data.accounts || data || []) as { id: string; platform: string; display_name: string }[];
    },
  });

  const connectedAccounts = accountsData || [];
  const connectedPlatformIds = new Set(connectedAccounts.map((a) => a.platform));

  // ── Handlers ──

  const handleConnectPlatform = async (platformId: string) => {
    setConnectingPlatform(platformId);
    try {
      const { data } = await socialAPI.connect(platformId);
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.success(t('onboard_platform_connected', '{{platform}} connected!', { platform: platformId }));
        refetchAccounts();
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('onboard_connect_failed', 'Failed to connect')));
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleFinish = async () => {
    setIsFinishing(true);
    try {
      await usersAPI.completeOnboarding({
        plan: selectedPlan,
        connected_accounts: connectedAccounts.map((a) => a.id),
      });
      // Update local user state
      if (user) {
        setUser({ ...user, plan: selectedPlan, onboarding_completed: true });
      }
      setStep('done');
    } catch (_err: unknown) {
      toast.error(t('onboard_complete_failed', 'Failed to complete onboarding'));
    } finally {
      setIsFinishing(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  // ── Step indicator ──

  const STEPS: { id: Step; label: string }[] = [
    { id: 'plan', label: t('onboard_choose_plan_step', 'Choose Plan') },
    { id: 'connect', label: t('onboard_connect_accounts_step', 'Connect Accounts') },
    { id: 'done', label: t('onboard_all_set_step', 'All Set') },
  ];

  const currentStepIndex = STEPS.findIndex((s) => s.id === step);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('onboard_welcome', 'Welcome to PlugD')}</h1>
              <p className="text-sm text-gray-500 mt-1">{t('onboard_welcome_desc', 'Let\'s get you set up in just a couple of steps')}</p>
            </div>
            {step !== 'done' && (
              <button
                onClick={handleFinish}
                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              >
                {t('onboard_skip', 'Skip for now')}
              </button>
            )}
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mt-5">
            {STEPS.map((s, i) => {
              const isComplete = i < currentStepIndex;
              const isCurrent = i === currentStepIndex;
              return (
                <React.Fragment key={s.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${isComplete
                          ? 'text-white'
                          : isCurrent
                            ? 'text-white'
                            : 'bg-gray-200 text-gray-400'
                        }`}
                      style={
                        isComplete || isCurrent
                          ? { backgroundColor: theme.hex.primary }
                          : undefined
                      }
                    >
                      {isComplete ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <span
                      className={`text-sm font-medium ${isCurrent ? 'text-gray-900' : isComplete ? 'text-gray-600' : 'text-gray-400'
                        }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 rounded ${i < currentStepIndex ? '' : 'bg-gray-200'
                        }`}
                      style={i < currentStepIndex ? { backgroundColor: theme.hex.primary } : undefined}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* ─── Step 1: Choose Plan ─── */}
        {step === 'plan' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-900">{t('onboard_choose_plan', 'Choose your plan')}</h2>
              <p className="text-sm text-gray-500 mt-1.5">
                {t('onboard_plan_desc', 'Start free and upgrade anytime. All plans include a 14-day trial of premium features.')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {PLANS.map((plan) => {
                const Icon = plan.icon;
                const isSelected = selectedPlan === plan.id;
                return (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`relative rounded-xl border-2 p-5 text-left transition-all hover:shadow-md ${isSelected
                        ? 'shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                    style={
                      isSelected
                        ? { borderColor: theme.hex.primary, backgroundColor: `${theme.hex.primary}08` }
                        : undefined
                    }
                  >
                    {plan.popular && (
                      <span
                        className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] font-bold text-white rounded-full"
                        style={{ backgroundColor: theme.hex.primary }}
                      >
                        {t('onboard_popular', 'POPULAR')}
                      </span>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${theme.hex.primary}15` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: theme.hex.primary }} />
                      </div>
                      <span className="font-bold text-gray-900">{plan.name}</span>
                    </div>
                    <div className="mb-3">
                      <span className="text-2xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-xs text-gray-500">{plan.period}</span>
                    </div>
                    <ul className="space-y-1.5">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-start gap-1.5 text-xs text-gray-600">
                          <Check
                            className="w-3 h-3 flex-shrink-0 mt-0.5"
                            style={{ color: theme.hex.primary }}
                          />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {isSelected && (
                      <div
                        className="mt-3 w-full py-1.5 rounded-lg text-center text-xs font-semibold text-white"
                        style={{ backgroundColor: theme.hex.primary }}
                      >
                        {t('onboard_selected', 'Selected')}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end mt-8">
              <button
                onClick={() => setStep('connect')}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: theme.hex.primary }}
              >
                {t('onboard_continue', 'Continue')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 2: Connect Accounts ─── */}
        {step === 'connect' && (
          <div>
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-900">{t('onboard_connect_accounts', 'Connect your social accounts')}</h2>
              <p className="text-sm text-gray-500 mt-1.5">
                {t('onboard_connect_accounts_desc', 'Link the platforms you want to publish to. You can always add more later.')}
              </p>
            </div>

            {/* Connected accounts summary */}
            {connectedAccounts.length > 0 && (
              <div className="mb-6 p-4 rounded-xl border border-emerald-200 bg-emerald-50">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-800">
                    {connectedAccounts.length} {t('onboard_account_text', 'account{{plural}} connected', { plural: connectedAccounts.length !== 1 ? 's' : '' })}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {connectedAccounts.map((acc) => (
                    <span
                      key={acc.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-full text-xs font-medium text-gray-700 border border-gray-200"
                    >
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                      {acc.display_name || acc.platform}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Platform grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {POPULAR_PLATFORMS.map((platform) => {
                const isConnected = connectedPlatformIds.has(platform.id);
                const isConnecting = connectingPlatform === platform.id;
                return (
                  <button
                    key={platform.id}
                    onClick={() => !isConnected && handleConnectPlatform(platform.id)}
                    disabled={isConnected || isConnecting}
                    className={`relative flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all ${isConnected
                        ? 'border-emerald-300 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                  >
                    <div
                      className={`w-12 h-12 ${platform.color} rounded-xl flex items-center justify-center text-white text-lg font-bold`}
                    >
                      {isConnecting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        platform.icon
                      )}
                    </div>
                    <span className="text-xs font-medium text-gray-700">{platform.name}</span>
                    {isConnected && (
                      <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-emerald-500" />
                    )}
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-gray-400 text-center mt-4">
              {t('onboard_more_platforms', 'More platforms available in Settings after setup')}
            </p>

            <div className="flex items-center justify-between mt-8">
              <button
                onClick={() => setStep('plan')}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← {t('onboard_back', 'Back')}
              </button>
              <button
                onClick={handleFinish}
                disabled={isFinishing}
                className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                style={{ backgroundColor: theme.hex.primary }}
              >
                {isFinishing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('onboard_finishing', 'Finishing...')}
                  </>
                ) : (
                  <>
                    {connectedAccounts.length > 0 ? t('onboard_finish_setup', 'Finish Setup') : t('onboard_skip_finish', 'Skip & Finish')}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 3: All Done ─── */}
        {step === 'done' && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div
              className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
              style={{ backgroundColor: `${theme.hex.primary}15` }}
            >
              <Rocket className="w-10 h-10" style={{ color: theme.hex.primary }} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('onboard_all_set', 'You\'re all set!')}</h2>
            <p className="text-gray-500 max-w-md mb-8">
              {t('onboard_all_set_desc', 'Your account is ready. Start creating content, scheduling posts, and growing your audience across platforms.')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleGoToDashboard}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: theme.hex.primary }}
              >
                {t('onboard_go_dashboard', 'Go to Dashboard')}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/compose')}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all"
              >
                {t('onboard_create_first_post', 'Create Your First Post')}
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
